<template>
  <div class="container-fluid mt-4" :class="themeClass">
    <div class="card shadow-sm">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          <i class="bi bi-bank me-2"></i>
          Cuentas Bancarias
        </h5>
        <button v-if="puedeCrear" class="btn btn-primary btn-sm" @click="abrirModalNuevo">
          <i class="bi bi-plus-circle me-1"></i> Nueva cuenta
        </button>
      </div>

      <div class="card-body">
        <div v-if="cargando" class="text-center py-4">
          <div class="spinner-border text-primary"></div>
        </div>
        <div v-else-if="error" class="alert alert-danger">
          <i class="bi bi-exclamation-triangle me-2"></i>{{ error }}
        </div>
        <div v-else>
          <div class="table-responsive">
            <table class="table table-hover align-middle">
              <thead class="table-light">
                <tr>
                  <th>Banco</th>
                  <th>N.° Cuenta</th>
                  <th>Tipo</th>
                  <th class="text-end">Saldo Inicial</th>
                  <th class="text-end">Saldo Actual</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="bancos.length === 0">
                  <td colspan="7" class="text-center py-4 text-muted">
                    <i class="bi bi-inbox me-2"></i>No hay cuentas bancarias configuradas
                  </td>
                </tr>
                <tr v-for="b in bancos" :key="b.id">
                  <td class="fw-semibold">{{ b.nombre_banco }}</td>
                  <td class="font-monospace">{{ b.numero_cuenta }}</td>
                  <td>
                    <span class="badge bg-info text-dark text-capitalize">{{ b.tipo_cuenta }}</span>
                  </td>
                  <td class="text-end text-muted">${{ fmt(b.saldo_inicial) }}</td>
                  <td class="text-end fw-bold text-success">${{ fmt(b.saldo_actual ?? b.saldo_inicial) }}</td>
                  <td>
                    <span :class="b.activo == 1 ? 'badge bg-success' : 'badge bg-danger'">
                      {{ b.activo == 1 ? 'Activa' : 'Inactiva' }}
                    </span>
                  </td>
                  <td>
                    <button
                      class="btn btn-sm btn-primary me-1"
                      title="Ver movimientos"
                      @click="verMovimientos(b)"
                    >
                      <i class="bi bi-list-ul me-1"></i>Movimientos
                    </button>
                    <button
                      v-if="puedeEditar"
                      class="btn btn-sm btn-outline-warning"
                      title="Editar"
                      @click="editarBanco(b)"
                    >
                      <i class="bi bi-pencil"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════
         MODAL: Nueva / Editar cuenta bancaria
    ═══════════════════════════════════════════════════════════ -->
    <div v-if="mostrarModal" class="modal d-block" tabindex="-1" style="background:rgba(0,0,0,.5)">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-bank me-2"></i>
              {{ editando ? 'Editar cuenta bancaria' : 'Nueva cuenta bancaria' }}
            </h5>
            <button type="button" class="btn-close" @click="cerrarModal"></button>
          </div>
          <form @submit.prevent="guardar">
            <div class="modal-body">
              <div class="alert alert-danger py-2 small" v-if="errorModal">{{ errorModal }}</div>

              <div class="mb-3">
                <label class="form-label fw-semibold">Nombre del banco <span class="text-danger">*</span></label>
                <input v-model="form.nombre_banco" type="text" class="form-control" required placeholder="Ej: Banco Pichincha" />
              </div>

              <div class="mb-3">
                <label class="form-label fw-semibold">Número de cuenta <span class="text-danger">*</span></label>
                <input v-model="form.numero_cuenta" type="text" class="form-control" required placeholder="Ej: 2200123456" />
              </div>

              <div class="mb-3">
                <label class="form-label fw-semibold">Tipo de cuenta</label>
                <select v-model="form.tipo_cuenta" class="form-select">
                  <option value="corriente">Corriente</option>
                  <option value="ahorros">Ahorros</option>
                </select>
              </div>

              <div class="mb-3">
                <label class="form-label fw-semibold">Saldo inicial ($)</label>
                <input v-model.number="form.saldo_inicial" type="number" min="0" step="0.01" class="form-control" />
              </div>

              <div v-if="editando" class="form-check mt-2">
                <input v-model="form.activo" type="checkbox" class="form-check-input" id="chkActivo" :true-value="1" :false-value="0" />
                <label class="form-check-label" for="chkActivo">Cuenta activa</label>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="cerrarModal">Cancelar</button>
              <button type="submit" class="btn btn-primary" :disabled="guardando">
                <span v-if="guardando" class="spinner-border spinner-border-sm me-1"></span>
                {{ guardando ? 'Guardando...' : 'Guardar' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import api from '@/services/api'
import themeManager from '@/store/theme'
import notificationService from '@/services/notificationService'

export default {
  name: 'ClientBancosCuentas',

  data() {
    return {
      cargando:     false,
      error:        null,
      bancos:       [],
      mostrarModal: false,
      editando:     false,
      guardando:    false,
      errorModal:   '',
      form: {
        id:            null,
        nombre_banco:  '',
        numero_cuenta: '',
        tipo_cuenta:   'corriente',
        saldo_inicial: 0,
        activo:        1,
      },
    }
  },

  computed: {
    themeClass() { return themeManager.themeClass },
    puedeCrear() {
      return this.$store.getters.puedoHacer('/finanzas/bancos', 'crear')
    },
    puedeEditar() {
      return this.$store.getters.puedoHacer('/finanzas/bancos', 'editar')
    },
  },

  async created() {
    await this.cargar()
  },

  methods: {
    async cargar() {
      this.cargando = true
      this.error    = null
      try {
        const { data } = await api.get('/bancos')
        this.bancos = data.bancos || []
      } catch {
        this.error = 'Error al cargar las cuentas bancarias'
      } finally {
        this.cargando = false
      }
    },

    abrirModalNuevo() {
      this.editando   = false
      this.errorModal = ''
      this.form = { id: null, nombre_banco: '', numero_cuenta: '', tipo_cuenta: 'corriente', saldo_inicial: 0, activo: 1 }
      this.mostrarModal = true
    },

    editarBanco(b) {
      this.editando   = true
      this.errorModal = ''
      this.form = {
        id:            b.id,
        nombre_banco:  b.nombre_banco,
        numero_cuenta: b.numero_cuenta,
        tipo_cuenta:   b.tipo_cuenta  || 'corriente',
        saldo_inicial: parseFloat(b.saldo_inicial || 0),
        activo:        parseInt(b.activo),
      }
      this.mostrarModal = true
    },

    cerrarModal() {
      this.mostrarModal = false
    },

    async guardar() {
      this.errorModal = ''
      this.guardando  = true
      try {
        if (this.editando) {
          await api.put(`/bancos/${this.form.id}`, this.form)
          notificationService.success('Cuenta bancaria actualizada')
        } else {
          await api.post('/bancos', this.form)
          notificationService.success('Cuenta bancaria creada')
        }
        this.cerrarModal()
        await this.cargar()
      } catch (e) {
        this.errorModal = e.response?.data?.error || 'Error al guardar la cuenta'
      } finally {
        this.guardando = false
      }
    },

    verMovimientos(b) {
      this.$router.push({ path: `/finanzas/bancos/${b.id}/movimientos` })
    },

    fmt(val) { return parseFloat(val || 0).toFixed(2) },
  },
}
</script>

